import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DatePicker } from "@/components/DatePicker";
import { api, errorMessage } from "@/lib/api";
import { PROMPT_LEVELS, todayISO } from "@/lib/aba";
import { SESSIONS } from "@/constants/testIds";

const emptyForm = () => ({ data: todayISO(), corrette: "", totale: "10", prompt: "indipendente", note: "" });

export const SessionDialog = ({ open, onOpenChange, action, session, nextNumber, onSaved }) => {
  const qc = useQueryClient();
  const isEdit = Boolean(session);
  const [form, setForm] = useState(emptyForm());

  useEffect(() => {
    if (!open) return;
    setForm(session ? { data: session.data, corrette: String(session.corrette), totale: String(session.totale), prompt: session.prompt || "indipendente", note: session.note || "" } : emptyForm());
  }, [open, session]);

  const corrette = Number(form.corrette);
  const totale = Number(form.totale);
  const valid = form.corrette !== "" && Number.isInteger(corrette) && Number.isInteger(totale) && totale > 0 && corrette >= 0 && corrette <= totale;
  const percent = valid ? Math.round((corrette / totale) * 1000) / 10 : null;

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["action", action.action_id] });
    qc.invalidateQueries({ queryKey: ["actions", action.patient_id] });
    qc.invalidateQueries({ queryKey: ["summary", action.patient_id] });
    qc.invalidateQueries({ queryKey: ["report", action.patient_id] });
    qc.invalidateQueries({ queryKey: ["patients"] });
    qc.invalidateQueries({ queryKey: ["reminders"] });
  };

  const mutation = useMutation({
    mutationFn: async () => {
      const payload = { data: form.data, corrette, totale, prompt: form.prompt, note: form.note };
      if (isEdit) return (await api.put(`/sessions/${session.session_id}`, payload)).data;
      return (await api.post(`/actions/${action.action_id}/sessions`, payload)).data;
    },
    onSuccess: (data) => {
      invalidate();
      const an = data?.analytics;
      if (!isEdit && an) {
        if (an.suggerisci_acquisito) toast.success(`Sessione ${data.session?.numero} salvata — criterio raggiunto per ${an.streak_criterio} sessioni consecutive!`);
        else if (an.complete && an.count === an.target) toast.success(`Sessione salvata: grafico completo con ${an.target} sessioni`);
        else toast.success(`Sessione ${data.session?.numero} salvata (${percent}%)`);
      } else {
        toast.success("Sessione aggiornata");
      }
      onOpenChange(false);
      onSaved?.(data);
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-card" data-testid={SESSIONS.dialog}>
        <DialogHeader>
          <DialogTitle className="font-heading">{isEdit ? `Modifica sessione ${session.numero}` : `Nuova sessione${nextNumber ? ` n. ${nextNumber}` : ""}`}</DialogTitle>
          <DialogDescription>Azione: <span className="font-medium text-foreground">{action?.nome}</span></DialogDescription>
        </DialogHeader>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (!valid) return toast.error("Controlla i valori: il totale deve essere > 0 e le corrette non possono superare il totale");
            mutation.mutate();
          }}
        >
          <div className="space-y-2">
            <Label>Data sessione</Label>
            <DatePicker value={form.data} onChange={(v) => setForm((f) => ({ ...f, data: v }))} testId={SESSIONS.dateButton} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Risposte corrette</Label>
              <Input type="number" min={0} step={1} inputMode="numeric" value={form.corrette} onChange={(e) => setForm((f) => ({ ...f, corrette: e.target.value }))} placeholder="0" className="bg-card h-11 text-lg font-heading tabular" data-testid={SESSIONS.corretteInput} autoFocus />
            </div>
            <div className="space-y-2">
              <Label>Totale prove</Label>
              <Input type="number" min={1} step={1} inputMode="numeric" value={form.totale} onChange={(e) => setForm((f) => ({ ...f, totale: e.target.value }))} className="bg-card h-11 text-lg font-heading tabular" data-testid={SESSIONS.totaleInput} />
            </div>
          </div>
          <div className={`rounded-lg px-4 py-3 flex items-center justify-between ${valid ? (percent >= (action?.criterio || 80) ? "bg-emerald-50 text-emerald-900" : "bg-accent text-foreground") : "bg-muted text-muted-foreground"}`} data-testid="session-live-percent">
            <span className="text-sm">Percentuale corrette</span>
            <span className="font-heading text-2xl font-semibold tabular">{valid ? `${percent}%` : "—"}</span>
          </div>
          <div className="space-y-2">
            <Label>Livello di aiuto (prompt) prevalente</Label>
            <div className="grid grid-cols-5 gap-1.5" role="radiogroup" data-testid="session-prompt-group">
              {PROMPT_LEVELS.map((l) => {
                const active = form.prompt === l.key;
                return (
                  <button
                    key={l.key}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    onClick={() => setForm((f) => ({ ...f, prompt: l.key }))}
                    data-testid={`session-prompt-${l.key}`}
                    className={`flex flex-col items-center gap-1 rounded-lg border px-1 py-2 text-[11px] leading-tight transition-colors ${active ? "bg-accent border-primary text-foreground" : "border-border text-muted-foreground hover:bg-muted"}`}
                    style={active ? { borderColor: l.color } : undefined}
                  >
                    <span className="h-6 w-6 rounded-full border-2 flex items-center justify-center text-xs font-bold" style={{ borderColor: l.color, color: active ? "#fff" : l.color, background: active ? l.color : "#fff" }}>{l.code}</span>
                    {l.short}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="space-y-2">
            <Label>Note (opzionale)</Label>
            <Textarea value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} placeholder="Comportamenti problema, rinforzatori, generalizzazione…" className="bg-card min-h-[60px]" data-testid={SESSIONS.noteInput} />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Annulla</Button>
            <Button type="submit" disabled={mutation.isPending || !valid} data-testid={SESSIONS.submitButton}>
              {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {isEdit ? "Salva modifiche" : "Registra sessione"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
